import React, { useState, useEffect, useRef } from 'react';

const CallingScreen = ({ onTimeout }) => {
  const [timeLeft, setTimeLeft] = useState(60); // 60 seconds countdown
  const audioElementRef = useRef(null);
  const intervalRef = useRef(null);
  const timerRef = useRef(null);

  // Generate beep sound as WAV data URL
  const generateBeepDataURL = () => {
    // Simple beep sound - 800Hz tone for 0.15 seconds
    const sampleRate = 22050;
    const duration = 0.15;
    const frequency = 800;
    const numSamples = Math.floor(sampleRate * duration);

    // Create WAV file header
    const header = new ArrayBuffer(44);
    const view = new DataView(header);

    // "RIFF" chunk descriptor
    view.setUint32(0, 0x52494646, false); // "RIFF"
    view.setUint32(4, 36 + numSamples * 2, true); // file size - 8
    view.setUint32(8, 0x57415645, false); // "WAVE"

    // "fmt " sub-chunk
    view.setUint32(12, 0x666d7420, false); // "fmt "
    view.setUint32(16, 16, true); // subchunk size
    view.setUint16(20, 1, true); // audio format (PCM)
    view.setUint16(22, 1, true); // number of channels (mono)
    view.setUint32(24, sampleRate, true); // sample rate
    view.setUint32(28, sampleRate * 2, true); // byte rate
    view.setUint16(32, 2, true); // block align
    view.setUint16(34, 16, true); // bits per sample

    // "data" sub-chunk
    view.setUint32(36, 0x64617461, false); // "data"
    view.setUint32(40, numSamples * 2, true); // data size

    // Generate audio samples
    const samples = new Int16Array(numSamples);
    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const envelope = Math.exp(-5 * t); // Decay envelope
      samples[i] = Math.floor(envelope * Math.sin(2 * Math.PI * frequency * t) * 8000);
    }

    // Combine header and samples
    const wavBytes = new Uint8Array(44 + numSamples * 2);
    wavBytes.set(new Uint8Array(header), 0);
    wavBytes.set(new Uint8Array(samples.buffer), 44);

    // Create blob and data URL
    const blob = new Blob([wavBytes], { type: 'audio/wav' });
    return URL.createObjectURL(blob);
  };

  // Function to play a beep sound
  const playPlop = async () => {
    try {
      if (!audioElementRef.current) {
        console.warn('CallingScreen: No Audio element available for playback');
        return;
      }

      // Reset and play the audio
      audioElementRef.current.currentTime = 0;
      const playPromise = audioElementRef.current.play();

      if (playPromise !== undefined) {
        playPromise.catch(err => {
          console.log('CallingScreen: Audio playback prevented:', err.message);
        });
      }
    } catch (err) {
      console.log('CallingScreen: Audio playback error:', err);
    }
  };

  // Initialize audio and set up intervals
  useEffect(() => {
    console.log('✅ CallingScreen: MOUNTED - Initializing audio and setting up intervals');
    let isMounted = true;

    // Create and initialize audio element
    try {
      console.log('CallingScreen: Creating Audio element for beeping');
      const audio = new Audio();
      const blobUrl = generateBeepDataURL();
      audio.src = blobUrl;
      audio.volume = 0.3; // Set volume to 30%
      audio.preload = 'auto';

      // Store blob URL for cleanup
      audio.dataset.blobUrl = blobUrl;

      audioElementRef.current = audio;

      console.log('CallingScreen: Audio element created, attempting first beep');

      // Try to play immediately to "unlock" audio (within user gesture context)
      audio.play()
        .then(() => {
          console.log('CallingScreen: Initial beep played successfully!');
          // Set up interval for subsequent beeps (2 seconds after first beep)
          intervalRef.current = setInterval(() => {
            if (isMounted && audioElementRef.current) {
              playPlop();
            }
          }, 2000);
        })
        .catch(err => {
          console.warn('CallingScreen: Initial beep blocked:', err.message);
          console.warn('CallingScreen: This is likely due to browser autoplay policy');
          // Still set up the interval, maybe later beeps will work
          intervalRef.current = setInterval(() => {
            if (isMounted && audioElementRef.current) {
              playPlop();
            }
          }, 2000);
        });

    } catch (error) {
      console.error('CallingScreen: Failed to create Audio element:', error);
    }

    // Countdown timer (independent of audio)
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          if (onTimeout) {
            onTimeout();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      console.log('❌ CallingScreen: UNMOUNTING - Cleaning up intervals, timers, and audio');
      isMounted = false;

      // Clear countdown timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      // Clear beeping interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      // Cleanup audio element
      if (audioElementRef.current) {
        const blobUrl = audioElementRef.current.dataset.blobUrl;
        audioElementRef.current.pause();
        audioElementRef.current.src = '';
        if (blobUrl) {
          URL.revokeObjectURL(blobUrl);
        }
        audioElementRef.current = null;
      }

      console.log('CallingScreen: All intervals and audio cleaned up');
    };
  }, [onTimeout]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="calling-screen-overlay">
      <div className="calling-screen">
      <style jsx>{`
        .calling-screen-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          pointer-events: none;
        }

        .calling-screen {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 32px 48px;
          background: linear-gradient(135deg,
            rgba(0, 0, 0, 0.95) 0%,
            rgba(20, 20, 30, 0.98) 100%);
          border-radius: 20px;
          border: 2px solid rgba(139, 92, 246, 0.4);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5),
                      0 0 40px rgba(139, 92, 246, 0.3);
          backdrop-filter: blur(20px);
          animation: fadeIn 0.5s ease-out, floatIn 0.6s ease-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes floatIn {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .calling-avatar {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.25rem;
          position: relative;
          animation: pulse-ring 2.5s ease-in-out infinite;
          box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.7),
                      0 8px 24px rgba(139, 92, 246, 0.5),
                      0 0 40px rgba(139, 92, 246, 0.3) inset;
        }

        .calling-avatar::before {
          content: '';
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          border: 2px solid rgba(139, 92, 246, 0.6);
          box-shadow: 0 0 16px rgba(139, 92, 246, 0.4);
          animation: ripple 2.5s cubic-bezier(0.4, 0, 0.2, 1) infinite;
        }

        .calling-avatar::after {
          content: '';
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          border: 2px solid rgba(139, 92, 246, 0.5);
          box-shadow: 0 0 16px rgba(139, 92, 246, 0.3);
          animation: ripple 2.5s cubic-bezier(0.4, 0, 0.2, 1) infinite 1.25s;
        }

        @keyframes pulse-ring {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.7),
                        0 15px 40px rgba(139, 92, 246, 0.4),
                        0 0 60px rgba(139, 92, 246, 0.2) inset;
          }
          50% {
            transform: scale(1.05);
            box-shadow: 0 0 0 10px rgba(139, 92, 246, 0),
                        0 20px 50px rgba(139, 92, 246, 0.5),
                        0 0 80px rgba(139, 92, 246, 0.3) inset;
          }
        }

        @keyframes ripple {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          100% {
            transform: scale(2);
            opacity: 0;
          }
        }

        .calling-avatar i {
          font-size: 2rem;
          color: white;
          position: relative;
          z-index: 1;
        }

        .calling-text {
          font-size: 1.2rem;
          font-weight: 600;
          color: white;
          margin-bottom: 0.75rem;
          animation: fade-pulse 2s infinite;
          white-space: nowrap;
        }

        @keyframes fade-pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.6;
          }
        }

        .calling-subtitle {
          font-size: 0.9rem;
          color: rgba(255, 255, 255, 0.7);
          margin-bottom: 1.5rem;
        }

        .countdown-timer {
          font-size: 1.5rem;
          font-weight: 700;
          color: #a78bfa;
          background: linear-gradient(135deg,
            rgba(139, 92, 246, 0.15) 0%,
            rgba(124, 58, 237, 0.08) 100%);
          padding: 10px 28px;
          border-radius: 10px;
          border: 2px solid rgba(139, 92, 246, 0.25);
          font-family: 'SF Mono', 'Courier New', monospace;
          letter-spacing: 2px;
          box-shadow: 0 8px 24px rgba(139, 92, 246, 0.2),
                      0 0 40px rgba(139, 92, 246, 0.1) inset;
          animation: timerGlow 2s ease-in-out infinite;
        }

        @keyframes timerGlow {
          0%, 100% {
            box-shadow: 0 8px 24px rgba(139, 92, 246, 0.2),
                        0 0 40px rgba(139, 92, 246, 0.1) inset;
          }
          50% {
            box-shadow: 0 8px 32px rgba(139, 92, 246, 0.3),
                        0 0 60px rgba(139, 92, 246, 0.15) inset;
          }
        }

        .dots-animation {
          display: inline-block;
          width: 30px;
        }

        .dots-animation span {
          animation: blink 1.4s infinite both;
        }

        .dots-animation span:nth-child(2) {
          animation-delay: 0.2s;
        }

        .dots-animation span:nth-child(3) {
          animation-delay: 0.4s;
        }

        @keyframes blink {
          0%, 80%, 100% {
            opacity: 0;
          }
          40% {
            opacity: 1;
          }
        }
      `}</style>

      <div className="calling-avatar">
        <i className="fas fa-robot"></i>
      </div>

      <div className="calling-text">
        Calling Olivio
        <span className="dots-animation">
          <span>.</span>
          <span>.</span>
          <span>.</span>
        </span>
      </div>

      <div className="calling-subtitle">
        Please wait while we connect you
      </div>

      <div className="countdown-timer">
        {formatTime(timeLeft)}
      </div>
      </div>
    </div>
  );
};

export default CallingScreen;
